import find from 'lodash/find';
import get from 'lodash/get';
import isFunction from 'lodash/isFunction';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import Data from '../../data';
import { WrappedDropdown } from '../input';
import LabelWrapper from '../LabelWrapper.component';
import WrappedTooltip from '../overlay/WrappedTooltip.component';

const {
	Carrier,
	CalculatedData,
	DeliveryConfirmation,
	DeliveryConfirmationOptions,
	ShipmentContents,
	PackageType,
} = Data;
const usCurrencyFormatter = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
});

export class TrackingField extends Component {
	constructor(props) {
		super(props);

		this.state = {
			trackingOptions: this.buildTrackingOptions(props),
		};
	}

	componentDidUpdate(prevProps) {
		const { serviceType, packageType, shipmentContents } = this.props;

		if (
			serviceType !== prevProps.serviceType ||
			packageType !== prevProps.packageType ||
			shipmentContents !== prevProps.shipmentContents
		) {
			this.updateTrackingOptions();
		}
	}

	/**
	 * Return an array of tracking options based on service-type, package-type and shipment-contents.
	 * @param {object} props
	 * @returns {array}
	 */
	buildTrackingOptions = ({ serviceType, packageType, shipmentContents }) => {
		const trackingOptions = CalculatedData.getDeliveryConfirmationOptions(
			serviceType,
			// pass in 'package' for drop-off no label needed and print at home label
			packageType === PackageType.DropOffNoLabelNeeded ||
				packageType === PackageType.PrintAtHomeLabel
				? PackageType.Package
				: packageType,
			shipmentContents
		);

		if (trackingOptions.length > 0) {
			// sort by rate in an ascending order
			return trackingOptions.sort(
				(option1, option2) => option1.rate - option2.rate
			);
		}

		// returns 'None' option
		return [
			find(DeliveryConfirmationOptions, ['value', DeliveryConfirmation.None]),
		];
	};

	/**
	 * Notify the caller about the tracking selection.
	 * @param {object} data Tracking value and selection data.
	 */
	notifyParent = data => {
		const { onChange } = this.props;

		if (isFunction(onChange)) {
			onChange(data);
		}
	};

	/**
	 * Rebuild the service options when props udpate.
	 */
	updateTrackingOptions = () => {
		const { value } = this.props;
		const trackingOptions = this.buildTrackingOptions(this.props);
		const freeTrackingOption = find(trackingOptions, ['rate', 0]);
		const noneTrackingOption = find(trackingOptions, [
			'value',
			DeliveryConfirmation.None,
		]);
		const selectedTrackingOption = find(trackingOptions, ['value', value]);

		this.setState(
			{
				trackingOptions,
			},
			() => {
				if (!isString(value) || value === DeliveryConfirmation.None) {
					// automatically selects the free tracking option if tracking has not been selected yet
					if (isObject(freeTrackingOption)) {
						this.notifyParent({
							value: freeTrackingOption.value,
							tracking: freeTrackingOption,
						});
					}
				} else if (!isObject(selectedTrackingOption)) {
					// if the selected tracking is no longer available, reset the value
					this.notifyParent({
						value: noneTrackingOption?.value,
					});
				} else {
					// notify the caller the selected tracking option data where the rate may be different
					this.notifyParent({
						value,
						tracking: selectedTrackingOption,
					});
				}
			}
		);
	};

	onChange = ({ target }) => {
		const { value } = target;

		const { trackingOptions } = this.state;

		this.notifyParent({
			value,
			tracking: find(trackingOptions, ['value', value]),
		});
	};

	/**
	 * Render the tracking cost content.
	 * @param {object}
	 * @returns {ReactElement}
	 */
	renderRate = ({ rate }) => {
		const { showRate } = this.props;

		if (showRate && isNumber(rate)) {
			return (
				<div className="tracking-cost table-cell">
					{rate === 0 ? (
						<span>Free</span>
					) : (
						<span>{usCurrencyFormatter.format(rate)}</span>
					)}
				</div>
			);
		}

		return null;
	};

	/**
	 * Render a tracking item template: "<confirmation name> <confirmation rate>".
	 * @param {object} data Delivery confirmation option data object.
	 * @returns {ReactElement}
	 */
	renderItemTemplate = data => {
		const { carrier, itemTemplate } = this.props;
		const tooltip =
			carrier === Carrier.FedEx && data.type === DeliveryConfirmation.Signature
				? 'Requires the signature of any person at the recipients address or from somebody nearby, such as a neighbor or building manager.'
				: data.tooltip;

		if (itemTemplate) {
			return itemTemplate({
				...data,
				tooltip,
			});
		}

		return (
			<>
				<div className="tracking-item">
					<div className="tracking-title table-cell">
						<span>{get(data, 'label')}</span>
					</div>
					{this.renderRate(data)}
				</div>
				<WrappedTooltip tooltip={tooltip} target="tracking-item" />
			</>
		);
	};

	render() {
		const { value, serviceType, packageType, showRate, ...restProps } =
			this.props;
		const { trackingOptions } = this.state;

		return (
			<div className="common-ui-tracking-field">
				<WrappedDropdown
					{...restProps}
					options={trackingOptions}
					value={value}
					panelClassName="tracking-dropdown-panel"
					itemTemplate={this.renderItemTemplate}
					onChange={this.onChange}
				/>
			</div>
		);
	}
}

TrackingField.defaultProps = {
	showRate: true,
};

TrackingField.propTypes = {
	// carrier
	carrier: PropTypes.string,

	// service type
	serviceType: PropTypes.string,

	// package type
	packageType: PropTypes.string,

	// shipment contents
	shipmentContents: PropTypes.oneOf(Object.values(ShipmentContents)),

	// `true` to show Tracking Cost
	showRate: PropTypes.bool,

	// Value of the component
	value: PropTypes.any,

	// The template of an option item
	itemTemplate: PropTypes.elementType,

	// The handler to handle selection change
	onChange: PropTypes.func.isRequired,
};

export const TrackingFieldWithLabel = LabelWrapper(
	TrackingField,
	'common-ui-tracking-field-wrapper'
);
